import sliderCss from 'blaze-slider/dist/blaze.css?url'

import {Link, createFileRoute, notFound} from '@tanstack/react-router'
import {HiMiniArrowRight} from 'react-icons/hi2'
import {useTranslations} from 'use-intl'
import {Suspense} from 'react'
import {
    ProductPreview,
    ProductsPreviewSkeletonLoader,
} from '~/components/collection-details/ProductPreview'
import ImageComposition from '~/components/ImageComposition'
import {getCollectionDetails} from '~/client/fn/collection'
import Breadcrumbs from '~/components/common/Breadcrumbs'
import useLocaleParams from '~/hooks/useLocaleParams'
import Card from '~/components/common/Card'
import seo from '~/utils/seo'

export const Route = createFileRoute('/$lang/_layout/collections/$collectionId')({
    component: RouteComponent,
    loader: async ({params, context}) => {
        const collection = await getCollectionDetails({
            data: {
                collectionId: params.collectionId,
                locale: context.locale,
            },
        })

        if (!collection) {
            throw notFound()
        }

        return collection
    },
    head: ({loaderData: collection}) => {
        if (!collection) {
            return {}
        }

        const fallbackImageSrc = collection.designs[0].image.src
        const metaTags = seo({
            title: collection.seo?.title ?? collection.name,
            description: collection.seo?.description ?? collection.description ?? undefined,
            image: {
                src:
                    collection.seo?.image ??
                    `${fallbackImageSrc}&width=500&height=250&backgroundColor=1c1d2c`,
                width: 500,
                height: 250,
            },
        })

        return {
            meta: metaTags.length > 0 ? metaTags : undefined,
            links: [
                {
                    rel: 'preload',
                    href: sliderCss,
                    as: 'style',
                },
                {
                    rel: 'stylesheet',
                    href: sliderCss,
                },
            ],
        }
    },
})

function RouteComponent() {
    const params = useLocaleParams()
    const t = useTranslations('collectionDetails')
    const collection = Route.useLoaderData()

    return (
        <div className="px-4 py-6 mx-auto max-w-screen-xl lg:py-12 sm:px-6">
            <Breadcrumbs
                className="mb-2 md:mb-6"
                items={[
                    {name: t('breadcrumbs.home'), to: '/$lang', params: params},
                    {name: t('breadcrumbs.collections'), to: '/$lang/collections', params: params},
                    {name: collection.name, to: '.'},
                ]}
            />
            <div className="mb-3 lg:mb-5">
                <h1 className="text-2xl font-medium">{collection.name}</h1>
                {!!collection.description?.length && (
                    <p className="text-gray-400">{collection.description}</p>
                )}
            </div>

            <div>
                <h2 className="sr-only">{t('designs.title')}</h2>
                <ul className="grid grid-cols-2 gap-4 lg:gap-6 md:grid-cols-4">
                    {collection?.designs?.map((design) => (
                        <li key={design?.id}>
                            <Link
                                className="block aspect-square h-full"
                                to="/$lang/products"
                                params={params}
                                search={{design: design?.id}}
                            >
                                <Card className="p-2 md:p-4 flex flex-col gap-2 justify-between items-center h-full">
                                    <div className="h-full w-auto px-4">
                                        <ImageComposition
                                            image={design?.image!}
                                            sizes="400px"
                                            className="select-none"
                                        />
                                    </div>
                                    <div className="text-center font-bold text-sm">
                                        {design?.name}
                                    </div>
                                </Card>
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>
            <div className="mt-8">
                <div className="flex items-center justify-between flex-wrap mb-2 gap-y-2">
                    <h2 className="text-2xl font-medium">{t('relatedProducts.title')}</h2>

                    <Link
                        className="flex flex-row items-center gap-1 font-medium text-green-600"
                        to="/$lang/products"
                        params={params}
                        search={{collection: `T${collection.id}`}}
                    >
                        <span>{t('relatedProducts.showAll')}</span>
                        <HiMiniArrowRight className="size-4" />
                    </Link>
                </div>
                <Suspense fallback={<ProductsPreviewSkeletonLoader />}>
                    <ProductPreview collectionId={collection.id} />
                </Suspense>
            </div>
        </div>
    )
}
